import type { Node, NodeProps } from '@xyflow/react';
import { memo } from 'react';
import Avatar from '@savvy-kit/components/avatar';
import { Card, CardContent } from '@savvy-kit/components/card';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';

import type { ProfessionalNodeRenderData } from './types';
import { SPECIALTY_LABELS } from '../constants';
import { CenteredHandles } from './node-handles';

export type ProfessionalNode = Node<ProfessionalNodeRenderData, 'professional'>;

function ProfessionalNodeCardComponent({ data }: NodeProps<ProfessionalNode>) {
  const specialtyLabel = data.specialties.map((specialty) => SPECIALTY_LABELS[specialty]).join(', ');

  return (
    <Card
      className={cn(
        'w-56 cursor-pointer rounded-lg border-light p-0 transition-all duration-200',
        data.focused && 'border-primary ring-2 ring-primary',
        data.faded && 'opacity-30',
      )}
    >
      <CenteredHandles />
      <CardContent className="flex items-start gap-2.5 p-3">
        <Avatar imageUrl={data.photoUrl} firstName={data.name} lastName="" size="lg" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <Text variant="subtitle-sm" color="dark" className="truncate">
            {data.name}
          </Text>
          <Text variant="caption" color="secondary" className="truncate">
            {specialtyLabel === '' ? 'Professional contact' : specialtyLabel}
          </Text>
        </div>
      </CardContent>
    </Card>
  );
}

// The parent rebuilds `data` on every render (even unrelated drag frames), so a default
// shallow-props comparison would never skip a re-render — compare the fields that actually
// affect this card's output instead.
export const ProfessionalNodeCard = memo(
  ProfessionalNodeCardComponent,
  (prev, next) =>
    prev.data.name === next.data.name &&
    prev.data.photoUrl === next.data.photoUrl &&
    prev.data.specialties === next.data.specialties &&
    prev.data.faded === next.data.faded &&
    prev.data.focused === next.data.focused,
);
